// src/services/scoring.js
// Detailed IPL-style fantasy scoring.
// This takes a single-match stat line and a scoringRules object (from Firebase, or hardcoded).

export function calcPlayerPoints(stat, rules) {
  if (!stat || !rules) return 0;

  let pts = 0;

  // Batting
  const bat = rules.batting || {};
  const runs = stat.bat?.runs ?? 0;
  const balls = stat.bat?.balls ?? 0;
  const fours = stat.bat?.fours ?? 0;
  const sixes = stat.bat?.sixes ?? 0;
  const out = !!stat.bat?.out;

  pts += runs * (bat.run ?? 0);
  pts += fours * (bat.fourBonus ?? 0);
  pts += sixes * (bat.sixBonus ?? 0);

  // milestones: highest achieved (not cumulative)
  if (Array.isArray(bat.milestones)) {
    let best = 0;
    for (const m of bat.milestones) {
      if (runs >= (m.minRuns ?? Infinity)) best = Math.max(best, m.points ?? 0);
    }
    pts += best;
  }

  // duck
  if (runs === 0 && out && (bat.duckPenaltyOnlyIfOut ?? true)) {
    pts += bat.duckPenalty ?? 0;
  }

  // strike rate
  const srCfg = bat.strikeRate || {};
  if (balls >= (srCfg.minBalls ?? 999999) && balls > 0) {
    const sr = (runs / balls) * 100;
    pts += bracketPoints(sr, srCfg.brackets);
  }

  // Bowling
  const bowl = rules.bowling || {};
  const overs = stat.bowl?.overs ?? 0;
  const runsCon = stat.bowl?.runs ?? 0;
  const wkts = stat.bowl?.wkts ?? 0;
  const maidens = stat.bowl?.maidens ?? 0;
  const dots = stat.bowl?.dots ?? 0;

  pts += wkts * (bowl.wicket ?? 0);
  pts += maidens * (bowl.maiden ?? 0);
  pts += dots * (bowl.dotBall ?? 0);

  if (Array.isArray(bowl.wicketMilestones)) {
    let best = 0;
    for (const m of bowl.wicketMilestones) {
      if (wkts >= (m.minWkts ?? Infinity)) best = Math.max(best, m.points ?? 0);
    }
    pts += best;
  }

  // economy
  const ecoCfg = bowl.economy || {};
  if (overs >= (ecoCfg.minOvers ?? 999999) && overs > 0) {
    const econ = runsCon / overs;
    pts += bracketPoints(econ, ecoCfg.brackets);
  }

  // Fielding
  const fld = rules.fielding || {};
  pts += (stat.fld?.catches ?? 0) * (fld.catch ?? 0);
  pts += (stat.fld?.stumpings ?? 0) * (fld.stumping ?? 0);
  pts += (stat.fld?.runoutDirect ?? 0) * (fld.runoutDirect ?? 0);
  pts += (stat.fld?.runoutAssist ?? 0) * (fld.runoutAssist ?? 0);

  // caps
  const minP = rules.caps?.minPoints;
  const maxP = rules.caps?.maxPoints;
  if (typeof minP === "number") pts = Math.max(minP, pts);
  if (typeof maxP === "number") pts = Math.min(maxP, pts);

  return Math.round(pts);
}

export function bracketPoints(value, brackets = []) {
  if (!Array.isArray(brackets)) return 0;
  for (const b of brackets) {
    const hasMin = typeof b.min === "number";
    const hasMax = typeof b.max === "number";
    if (hasMin && value < b.min) continue;
    if (hasMax && value > b.max) continue;
    return b.points ?? 0;
  }
  return 0;
}
